import React from 'react';
import { Link } from 'react-admin';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { orange } from '@mui/material/colors';

import ListIcon from '@mui/icons-material/List';


const sx = {
	root: {
		padding: theme => theme.spacing(3),
		display: 'inline-block',
		width: '100%',
		textDecoration: 'none',
		border: '1px solid #E0E0E0',
		boxShadow: 'none !important'
	},
	content: {
		alignItems: 'center',
		display: 'flex'
	},
	title: {
		color: theme => theme.palette.text.secondary,
		fontWeight: 700,
		paddingRight: '10px'
	},
	value: {
		marginTop: theme => theme.spacing(),
		color: theme => theme.palette.text.primary,
	},
	iconWrapper: {
		alignItems: 'center',
		backgroundColor: theme => theme.palette.primary.main,
		borderRadius: '50%',
		display: 'inline-flex',
		height: '4rem',
		justifyContent: 'center',
		marginLeft: 'auto',
		width: '4rem',
		'& svg': {
			color: theme => theme.palette.common.white,
			fontSize: '2rem',
			height: '2rem',
			width: '2rem'
		}
	},
	icon: {
		color: theme => theme.palette.common.white,
		fontSize: '2rem',
		height: '2rem',
		width: '2rem'
	}
};

const paperRoundedSx = {
	border: '1px solid #E0E0E0',
	borderRadius: '20px',
	boxShadow: 'none !important'
};

const smallPaperSx = { padding: '12px !important' };
const iconWrapperSmallSx = { width: '3.5rem !important', height: '3.5rem !important' };
const warningIconSx = { backgroundColor: `${orange[500]} !important` };
const dividerSx = { marginTop: '16px' };
const contentSx = { margin: '0 -24px', marginBottom: '-24px', display: 'flex', flexDirection: 'column', flex: 1 };

const IconCard = ({ title, value, icon = <ListIcon />, variant, size, warning, color, content, linkTo, children, ...props }) => {
	let paperSx = { ...props.sx };
	let iconWrapperSx = {};
	if (props.paperSx) {
		paperSx = { ...paperSx, ...props.paperSx };
	}
	if (variant === 'rounded') {
		paperSx = { ...paperSx, ...paperRoundedSx };
	}
	if (size === 'small') {
		paperSx = { ...paperSx, ...smallPaperSx };
		iconWrapperSx = { ...iconWrapperSx, ...iconWrapperSmallSx };
	}
	if (warning) {
		iconWrapperSx = { ...iconWrapperSx, ...warningIconSx };
	}
	if (color) {
		iconWrapperSx = { ...iconWrapperSx, backgroundColor: `${color} !important` };
	}

	const renderCardContent = () => (
		<Box sx={sx.content}>
			<Box sx={sx.details}>
				<Typography sx={sx.title} variant="body1">{title}</Typography>
				{value ? <Typography sx={sx.value} variant="h4">{value}</Typography> : null}
				{children}
			</Box>
			{icon && (
				<Box sx={[sx.iconWrapper, iconWrapperSx]}>
					{icon}
				</Box>
			)}
		</Box>
	);

	return (
		<Paper sx={[sx.root, paperSx]}>
			{linkTo ? <Link to={linkTo} sx={{ textDecoration: 'none' }}>{renderCardContent()}</Link> : renderCardContent()}
			{content && (
				<Box sx={contentSx}>
					<Divider sx={dividerSx} />
					{content}
				</Box>
			)}
		</Paper>
	);
};

IconCard.propTypes = {
	icon: PropTypes.element,
	title: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default IconCard;
